import React, { FC, useState, useCallback } from "react";
import { Grid } from "@getregistered/greg-editors";
import { gridLoader } from "../../domain/grid/resultLoader";
import { PaginatedApplicationPrimerGrid } from "./PaginatedApplicationPrimerGrid";
import { DEFAULT_PER_PAGE, DEFAULT_PER_PAGE_STORAGE_KEY } from "./constants";
import { fetchJson } from "../railsFetch";
import { showToast } from "./gridToast";

export interface ReorderablePrimerGridProps {
  grid: Grid;
  resultsUrl: string;
  reorderUrl: string;
  actionCellRenderer?: 'menu' | 'buttons';
  showActions?: boolean;
  defaultPerPage?: number;
}

export const ReorderablePrimerGrid: FC<ReorderablePrimerGridProps> = ({
  grid,
  resultsUrl,
  reorderUrl,
  actionCellRenderer = 'menu',
  showActions = true,
  defaultPerPage
}) => {
  const loader = gridLoader(resultsUrl, resultsUrl, grid.columns);
  const [resultsCacheKey, setResultsCacheKey] = useState(0);

  const handleReorder = useCallback(async (orderedIds: (string | number)[]) => {
    // The server rejects any list that doesn't cover the whole scope (a
    // filtered or paginated grid submits only its visible rows). Surface the
    // rejection and refetch so the dragged row snaps back to the saved order
    // instead of sitting where it was dropped until the next reload.
    const failReorder = () => {
      showToast(
        "Could not save this order. Reordering needs the full list visible - clear any filters and show all rows, then try again.",
        "danger"
      );
      setResultsCacheKey((prev) => prev + 1);
    };

    try {
      const response = await fetchJson(reorderUrl, {
        method: "PATCH",
        body: JSON.stringify({ ordered_ids: orderedIds }),
      });
      if (!response.ok) failReorder();
    } catch {
      failReorder();
    }
  }, [reorderUrl]);

  return (
    <PaginatedApplicationPrimerGrid
      grid={grid}
      loader={loader}
      defaultPerPage={defaultPerPage || DEFAULT_PER_PAGE}
      perPageStorageKey={DEFAULT_PER_PAGE_STORAGE_KEY}
      page={1}
      reorderable={true}
      onReorder={handleReorder}
      actionCellRenderer={actionCellRenderer}
      showActions={showActions}
      resultsCacheKey={resultsCacheKey}
    />
  );
};
